import type { Key } from '@vizit/atoms';
import { useDebounce } from '@vizit/hooks';
import { type CategoryItem, ComboBox } from '@vizit/molecules';
import * as React from 'react';

type InputFieldState = {
  /**
   * Currently selected key
   *
   * @optional
   * @type {Key | null}
   */
  selectedKey?: Key | null;
  /**
   * Current input value
   *
   * @type {string}
   */
  inputValue: string;
};

interface ProductCategoryComboBoxProps {
  /**
   * The default value for the combo box.
   */
  defaultValue?: Key | null;
  /**
   * The menu trigger for the combo box.
   */
  menuTrigger?: 'input' | 'focus';
  /**
   * The label for the combo box.
   */
  label?: string;
  /**
   * The class name for the combo box.
   */
  className?: string;
  /**
   * The filter text for the combo box.
   */
  filterText: string;
  /**
   * The items for the combo box.
   */
  items: CategoryItem[];
  /**
   * Whether the combo box is loading.
   */
  isLoading: boolean;
  /**
   * The selected key for the combo box.
   */
  selectedKey?: Key | null;
  /**
   * The function to set the filter text.
   */
  setFilterText: (text: string) => void;
  /**
   * The function to load more items.
   */
  loadMore: () => void;
  /**
   * The function to handle the selection change.
   */
  onSelectionChange?: (key: Key | null) => void;
}

/**
 * Product category combo box component with infinite scrolling and filtering capabilities.
 * This is an uncontrolled component that manages its own state internally.
 *
 * For form integration with react-hook-form, use FormProductCategoryComboBox instead.
 *
 * @param props - Component props
 * @returns JSX element
 */
function ProductCategoryComboBox(props: ProductCategoryComboBoxProps) {
  const {
    defaultValue,
    items,
    isLoading,
    filterText,
    selectedKey,
    menuTrigger = 'focus',
    setFilterText,
    loadMore,
    onSelectionChange,
    ...other
  } = props;

  // default values
  const defaultSelectedKey = React.useMemo(
    () => (defaultValue || selectedKey ? defaultValue || selectedKey : undefined),
    [defaultValue, selectedKey],
  );
  const defaultInputValue = React.useMemo(
    () => (defaultSelectedKey ? items.find((item) => item.id === defaultSelectedKey)?.name : ''),
    [defaultSelectedKey, items],
  );

  const [inputFieldState, setInputFieldState] = React.useState<InputFieldState>({
    selectedKey: defaultSelectedKey || selectedKey || null,
    inputValue: defaultInputValue || filterText || '',
  });

  const [localFilterText, setLocalFilterText] = React.useState(filterText || '');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  // Only update the parent's filterText when debounced value changes
  React.useEffect(() => {
    setFilterText(debouncedFilterText);
  }, [debouncedFilterText, setFilterText]);

  const handleOnSelectionChange = (key: Key | null) => {
    const inputValue = items.find((item) => item.id === key)?.name || '';

    setInputFieldState({
      selectedKey: key,
      inputValue,
    });

    onSelectionChange?.(key);
  };

  const handleOnInputChange = (inputValue: string) => {
    setInputFieldState((prevState: InputFieldState) => ({
      inputValue,
      selectedKey: inputValue === '' ? null : prevState.selectedKey,
    }));

    setLocalFilterText(inputValue);
  };

  // Sync field value with input value
  React.useEffect(() => {
    if (!selectedKey) {
      setInputFieldState({
        selectedKey: null,
        inputValue: '',
      });

      setLocalFilterText('');
      setFilterText('');
    }
  }, [selectedKey, setFilterText]);

  const handleOnOpenChange = (isOpen: boolean) => {
    if (!isOpen && !inputFieldState.selectedKey) {
      setInputFieldState({ selectedKey: null, inputValue: '' });
      setLocalFilterText('');
      setFilterText('');
    }
  };

  const renderEmptyState = React.useCallback(() => {
    if (isLoading) return null;
    return <ComboBox.Empty>No categories found</ComboBox.Empty>;
  }, [isLoading]);

  return (
    <ComboBox<CategoryItem>
      aria-label="Product category filter"
      isLoading={isLoading}
      inputValue={inputFieldState.inputValue}
      onInputChange={handleOnInputChange}
      onOpenChange={handleOnOpenChange}
      onLoadMore={loadMore}
      items={items}
      onSelectionChange={handleOnSelectionChange}
      selectedKey={inputFieldState.selectedKey}
      placeholder="All categories"
      inputClassName="placeholder:text-primary"
      menuTrigger={menuTrigger}
      renderEmptyState={renderEmptyState}
      popoverProps={{
        shouldFlip: false,
      }}
      {...other}
    >
      {(item: CategoryItem) => <ComboBox.ProductCategoryItem key={item.id} {...item} />}
    </ComboBox>
  );
}

export { ProductCategoryComboBox };
export type { ProductCategoryComboBoxProps };
